import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import AppShell, { PageContainer } from "@/components/AppShell";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { usePresence } from "@/lib/PresenceContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Users, Plus, Crown, Check, X, LogOut, Trash2, UserMinus,
  Gamepad2, Swords, Loader2, ShieldCheck, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PresenceDot from "@/components/PresenceDot";
import Avatar from "@/components/Avatar";

const MAX_NAME = 32;
const SIZE_OPTIONS = [2, 3, 4, 5, 6, 8];
const MODE_OPTIONS = [
  { id: "casual", de: "Casual", en: "Casual" },
  { id: "ranked", de: "Ranked", en: "Ranked" },
];

export default function SquadsPage() {
  const { ready, supabaseUserId } = useSupabaseSession();
  const { isOnline } = usePresence();
  const { lang } = useT();
  const isDE = (lang || "de").startsWith("de");

  const [tab, setTab] = useState("mine"); // mine | invites
  const [mySquads, setMySquads] = useState([]);   // [{...squad, members:[{user_id, role, profile}]}]
  const [invites, setInvites] = useState([]);     // pending squad_invites I received
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  // -------- Loaders --------
  const loadSquads = useCallback(async () => {
    if (!ready || !supabase || !supabaseUserId) return;
    try {
      // 1) squads I'm a member of (RLS handles the filter, but we hint with a join)
      const { data: memberRows } = await supabase
        .from("squad_members")
        .select("squad_id, role, joined_at")
        .eq("user_id", supabaseUserId);
      const squadIds = (memberRows || []).map((r) => r.squad_id);
      if (squadIds.length === 0) {
        setMySquads([]);
        return;
      }
      const { data: squads } = await supabase
        .from("squads")
        .select("id, name, owner_id, game, mode, max_size, created_at")
        .in("id", squadIds);

      // 2) Members of those squads
      const { data: allMembers } = await supabase
        .from("squad_members")
        .select("squad_id, user_id, role, joined_at")
        .in("squad_id", squadIds);

      const userIds = [...new Set((allMembers || []).map((m) => m.user_id))];
      let profMap = {};
      if (userIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, username, avatar_url, country, is_premium")
          .in("user_id", userIds);
        (profs || []).forEach((p) => { profMap[p.user_id] = p; });
      }

      const enriched = (squads || []).map((s) => {
        const members = (allMembers || [])
          .filter((m) => m.squad_id === s.id)
          .map((m) => ({ ...m, profile: profMap[m.user_id] || { username: "user" } }));
        return { ...s, members };
      }).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

      setMySquads(enriched);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[squads] load failed", e);
    }
  }, [ready, supabaseUserId]);

  const loadInvites = useCallback(async () => {
    if (!ready || !supabase || !supabaseUserId) return;
    try {
      const { data } = await supabase
        .from("squad_invites")
        .select("id, inviter_id, invitee_id, squad_id, game, mode, message, status, created_at")
        .eq("invitee_id", supabaseUserId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      const rows = data || [];
      const inviterIds = [...new Set(rows.map((r) => r.inviter_id))];
      const squadIds = [...new Set(rows.map((r) => r.squad_id).filter(Boolean))];
      const [profs, squads] = await Promise.all([
        inviterIds.length
          ? supabase.from("profiles").select("user_id, username, avatar_url, country, is_premium").in("user_id", inviterIds)
          : Promise.resolve({ data: [] }),
        squadIds.length
          ? supabase.from("squads").select("id, name, game, mode, max_size").in("id", squadIds)
          : Promise.resolve({ data: [] }),
      ]);
      const pMap = {}; (profs.data || []).forEach((p) => { pMap[p.user_id] = p; });
      const sMap = {}; (squads.data || []).forEach((s) => { sMap[s.id] = s; });
      setInvites(rows.map((r) => ({ ...r, inviter: pMap[r.inviter_id], squad: sMap[r.squad_id] })));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[squads] invites load failed", e);
    }
  }, [ready, supabaseUserId]);

  useEffect(() => {
    if (!ready || !supabaseUserId) return;
    setLoading(true);
    Promise.all([loadSquads(), loadInvites()]).finally(() => setLoading(false));
  }, [ready, supabaseUserId, loadSquads, loadInvites]);

  // -------- Realtime: my membership rows + invites --------
  useEffect(() => {
    if (!ready || !supabase || !supabaseUserId) return;
    const ch = supabase.channel(`squads:${supabaseUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "squad_members", filter: `user_id=eq.${supabaseUserId}` },
        () => loadSquads(),
      )
      .on("postgres_changes",
        { event: "*", schema: "public", table: "squad_invites", filter: `invitee_id=eq.${supabaseUserId}` },
        () => loadInvites(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [ready, supabaseUserId, loadSquads, loadInvites]);

  // Realtime for roster updates within squads I'm in
  useEffect(() => {
    if (!ready || !supabase || mySquads.length === 0) return;
    const channels = mySquads.map((s) => {
      return supabase.channel(`squad-roster:${s.id}`)
        .on("postgres_changes",
          { event: "*", schema: "public", table: "squad_members", filter: `squad_id=eq.${s.id}` },
          () => loadSquads(),
        )
        .subscribe();
    });
    return () => { channels.forEach((c) => supabase.removeChannel(c)); };
  }, [ready, mySquads.map((s) => s.id).join(","), loadSquads]); // eslint-disable-line

  // -------- Actions --------
  const acceptInvite = async (inv) => {
    setInvites((l) => l.filter((x) => x.id !== inv.id));
    try {
      const { data, error } = await supabase.rpc("accept_squad_invite", { invite_id: inv.id });
      if (error) throw error;
      toast.success(isDE ? "Squad beigetreten!" : "Joined the squad!");
      // Fire notification back to inviter
      api.post("/squads/notify", {
        invitee_user_id: inv.inviter_id,
        kind: "squad_accepted",
        squad_name: inv.squad?.name,
      }).catch(() => {});
      await loadSquads();
      setSelectedId(data);
      setTab("mine");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[squads] accept failed", e);
      const detail = e?.message || "";
      const friendly = detail.includes("invite has no squad")
        ? (isDE ? "Bitte Datenbank-Migration v2.2 anwenden (siehe Admin)" : "Apply DB migration v2.2 first")
        : (isDE ? "Beitritt fehlgeschlagen" : "Couldn't join");
      toast.error(friendly);
      loadInvites();
    }
  };

  const declineInvite = async (inv) => {
    setInvites((l) => l.filter((x) => x.id !== inv.id));
    try {
      await supabase.from("squad_invites").update({ status: "declined" }).eq("id", inv.id);
    } catch (_e) { loadInvites(); }
  };

  const leaveSquad = async (squadId) => {
    if (!confirm(isDE ? "Squad wirklich verlassen?" : "Leave this squad?")) return;
    try {
      const { error } = await supabase.rpc("leave_squad", { squad_id: squadId });
      if (error) throw error;
      toast.success(isDE ? "Squad verlassen" : "Left the squad");
      setSelectedId(null);
      await loadSquads();
    } catch (e) {
      toast.error(isDE ? "Verlassen fehlgeschlagen" : "Couldn't leave");
    }
  };

  const deleteSquad = async (squadId) => {
    if (!confirm(isDE ? "Squad endgültig löschen? Dies kann nicht rückgängig gemacht werden." : "Permanently delete this squad?")) return;
    try {
      // Use the authoritative backend endpoint — bypasses RLS / missing-RPC
      // edge cases. Backend resolves cascade via the service-role client.
      await api.delete(`/squads/${squadId}`);
      toast.success(isDE ? "Squad gelöscht" : "Squad deleted");
      setSelectedId(null);
      await loadSquads();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[squads] delete failed", e?.response?.status, e?.response?.data);
      const code = e?.response?.data?.detail?.error || e?.response?.data?.detail;
      const friendly =
        code === "not_owner" ? (isDE ? "Nur der Owner darf löschen" : "Only the owner can delete")
        : code === "squad_not_found" ? (isDE ? "Squad nicht gefunden" : "Squad not found")
        : code === "auth_required" ? (isDE ? "Bitte erneut einloggen" : "Please sign in again")
        : code === "account_deactivated" ? (isDE ? "Dieses Konto wurde deaktiviert" : "Account deactivated")
        : (isDE ? "Löschen fehlgeschlagen" : "Delete failed");
      toast.error(friendly);
    }
  };

  const kickMember = async (squadId, targetUserId) => {
    try {
      const { error } = await supabase.rpc("kick_squad_member", {
        p_squad_id: squadId, p_target: targetUserId,
      });
      if (error) throw error;
      toast.success(isDE ? "Mitglied entfernt" : "Member kicked");
      await loadSquads();
    } catch (_e) {
      toast.error(isDE ? "Aktion fehlgeschlagen" : "Action failed");
    }
  };

  const selectedSquad = useMemo(
    () => mySquads.find((s) => s.id === selectedId) || mySquads[0],
    [mySquads, selectedId],
  );

  if (!ready) {
    return (
      <AppShell>
        <PageContainer>
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 p-10 text-center text-zinc-400">
            {isDE ? "Verbindung wird hergestellt…" : "Connecting…"}
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageContainer className="!max-w-6xl">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold inline-flex items-center gap-2" data-testid="squads-title">
              <Swords size={22} className="text-neon" /> {isDE ? "Squads" : "Squads"}
            </h1>
            <p className="text-xs text-zinc-500 mt-1">
              {isDE
                ? "Erstelle Squads, lade Freunde ein und tracke euren Roster in Echtzeit."
                : "Create squads, invite friends, and track your roster in real-time."}
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
            data-testid="squads-create-cta"
          >
            <Plus size={14} className="mr-2" /> {isDE ? "Squad erstellen" : "Create squad"}
          </Button>
        </header>

        {/* Tabs */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {[
            { k: "mine", de: "Meine Squads", en: "My squads", n: mySquads.length },
            { k: "invites", de: "Einladungen", en: "Invites", n: invites.length },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`squads-tab-${t.k}`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
                tab === t.k ? "bg-neon/15 text-neon border border-neon/40" : "bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/5"
              }`}
            >
              {isDE ? t.de : t.en}
              {t.n > 0 && (
                <span className="min-w-[18px] h-4 px-1 rounded-full bg-rose-500/80 text-white text-[10px] font-bold grid place-items-center" data-testid={`squads-tab-${t.k}-count`}>
                  {t.n}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading && (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm" data-testid="squads-loading">
            <Loader2 className="animate-spin inline mr-2" size={14} /> {isDE ? "Lade…" : "Loading…"}
          </div>
        )}

        {!loading && tab === "mine" && (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
            {/* Sidebar list */}
            <aside className="rounded-2xl border border-white/5 bg-zinc-900/50 p-2" data-testid="squads-list">
              {mySquads.length === 0 ? (
                <p className="text-xs text-zinc-500 p-4 text-center">
                  {isDE ? "Du bist noch in keinem Squad." : "You're not in any squad yet."}
                </p>
              ) : mySquads.map((s) => {
                const active = (selectedSquad?.id === s.id);
                const isOwner = s.owner_id === supabaseUserId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    data-testid={`squad-item-${s.id}`}
                    className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors flex items-center gap-3 ${
                      active ? "bg-neon/10 border border-neon/30" : "hover:bg-white/5 border border-transparent"
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg grid place-items-center text-xs font-display font-black shrink-0 ${
                      isOwner ? "bg-gradient-to-br from-neon to-blue-500 text-black" : "bg-zinc-800 text-zinc-300"
                    }`}>
                      {(s.name || "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate inline-flex items-center gap-1.5">
                        {s.name}
                        {isOwner && <Crown size={11} className="text-amber-400 shrink-0" />}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">
                        {s.members.length}/{s.max_size} · {s.game || (isDE ? "Spiel offen" : "any game")}
                      </div>
                    </div>
                  </button>
                );
              })}
            </aside>

            {/* Detail */}
            <div>
              {selectedSquad ? (
                <SquadDetail
                  squad={selectedSquad}
                  isDE={isDE}
                  myId={supabaseUserId}
                  isOnline={isOnline}
                  onLeave={() => leaveSquad(selectedSquad.id)}
                  onDelete={() => deleteSquad(selectedSquad.id)}
                  onKick={(uid) => kickMember(selectedSquad.id, uid)}
                  onInvited={loadSquads}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-white/10 bg-zinc-900/30 p-10 text-center">
                  <Users size={28} className="text-neon mx-auto mb-2" />
                  <h3 className="font-display font-bold text-lg">
                    {isDE ? "Noch kein Squad ausgewählt" : "No squad selected"}
                  </h3>
                  <p className="text-sm text-zinc-500 mt-1 mb-4 max-w-md mx-auto">
                    {isDE
                      ? "Erstelle deinen ersten Squad und lade Freunde ein — Roster-Updates kommen in Echtzeit."
                      : "Create your first squad and invite friends — roster updates land in real-time."}
                  </p>
                  <Button onClick={() => setCreateOpen(true)} className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold" data-testid="squads-create-empty">
                    <Plus size={14} className="mr-2" /> {isDE ? "Squad erstellen" : "Create squad"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && tab === "invites" && (
          <div className="rounded-2xl border border-white/5 bg-zinc-900/50 overflow-hidden" data-testid="squads-invites-list">
            {invites.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500" data-testid="squads-invites-empty">
                {isDE ? "Keine offenen Einladungen" : "No pending invites"}
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {invites.map((inv) => (
                  <li key={inv.id} className="px-4 py-3 flex items-center gap-3" data-testid={`squad-invite-${inv.id}`}>
                    <Avatar src={inv.inviter?.avatar_url} name={inv.inviter?.username} size={40} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <span className="font-semibold">{inv.inviter?.username || "user"}</span>
                        <span className="text-zinc-400"> {isDE ? "lädt dich ein:" : "invites you to:"} </span>
                        <span className="font-semibold text-neon">{inv.squad?.name || "—"}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate inline-flex items-center gap-1.5 mt-0.5">
                        <Gamepad2 size={10} />
                        {inv.squad?.game || (isDE ? "Spiel offen" : "any game")}
                        {inv.squad?.mode && <> · <span className="capitalize">{inv.squad.mode}</span></>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => acceptInvite(inv)}
                      className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-8 text-xs"
                      data-testid={`squad-invite-accept-${inv.id}`}
                    >
                      <Check size={12} className="mr-1" /> {isDE ? "Annehmen" : "Accept"}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => declineInvite(inv)}
                      className="text-zinc-400 hover:text-rose-400 h-8 w-8 p-0"
                      data-testid={`squad-invite-decline-${inv.id}`}
                    >
                      <X size={14} />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {createOpen && (
          <CreateSquadModal
            isDE={isDE}
            onClose={() => setCreateOpen(false)}
            onCreated={async (id) => { await loadSquads(); setSelectedId(id); setTab("mine"); }}
            ownerId={supabaseUserId}
          />
        )}
      </PageContainer>
    </AppShell>
  );
}

// ============================================================================
// Squad detail panel
// ============================================================================
function SquadDetail({ squad, isDE, myId, isOnline, onLeave, onDelete, onKick, onInvited }) {
  const isOwner = squad.owner_id === myId;
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <section className="rounded-2xl border border-white/10 bg-zinc-900/50 overflow-hidden" data-testid={`squad-detail-${squad.id}`}>
      <div className="bg-gradient-to-br from-fuchsia-500/10 via-cyan-500/10 to-emerald-500/10 px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black font-display font-black text-xl shrink-0">
            {(squad.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-xl truncate inline-flex items-center gap-2" data-testid="squad-detail-name">
              {squad.name}
              {isOwner && (
                <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300 inline-flex items-center gap-1 text-[10px]">
                  <Crown size={10} /> {isDE ? "Owner" : "Owner"}
                </Badge>
              )}
            </h2>
            <div className="text-xs text-zinc-400 inline-flex items-center gap-1.5 mt-0.5">
              <Gamepad2 size={11} />
              {squad.game || (isDE ? "Spiel offen" : "any game")}
              {squad.mode && <> · <span className="capitalize">{squad.mode}</span></>}
              <> · {squad.members.length}/{squad.max_size}</>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm" onClick={() => setInviteOpen(true)}
            disabled={squad.members.length >= squad.max_size}
            className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-8 text-xs disabled:opacity-50"
            data-testid="squad-invite-cta"
          >
            <Plus size={12} className="mr-1" /> {isDE ? "Einladen" : "Invite"}
          </Button>
          {isOwner ? (
            <Button size="sm" variant="ghost" onClick={onDelete} className="text-rose-300 hover:text-rose-200 h-8 text-xs" data-testid="squad-delete-cta">
              <Trash2 size={12} className="mr-1" /> {isDE ? "Löschen" : "Delete"}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={onLeave} className="text-zinc-300 hover:text-rose-300 h-8 text-xs" data-testid="squad-leave-cta">
              <LogOut size={12} className="mr-1" /> {isDE ? "Verlassen" : "Leave"}
            </Button>
          )}
        </div>
      </div>

      {/* Roster */}
      <ul className="divide-y divide-white/5" data-testid="squad-roster">
        {squad.members.map((m) => {
          const p = m.profile || {};
          const live = isOnline?.(m.user_id);
          const isLeader = m.role === "leader" || m.user_id === squad.owner_id;
          const isMe = m.user_id === myId;
          return (
            <li key={m.user_id} className="flex items-center gap-3 px-4 py-3" data-testid={`squad-member-${m.user_id}`}>
              <div className="relative shrink-0">
                <Avatar src={p.avatar_url} name={p.username} size={44} />
                {live && <PresenceDot userId={m.user_id} size={12} className="absolute -bottom-0.5 -right-0.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/u/${encodeURIComponent(p.username || "")}`} className="block font-semibold text-sm hover:text-neon transition-colors truncate inline-flex items-center gap-1.5">
                  {p.username || "user"}
                  {isLeader && <Crown size={11} className="text-amber-400" />}
                  {isMe && <span className="text-[10px] font-mono text-zinc-500">({isDE ? "du" : "you"})</span>}
                </Link>
                <div className="flex items-center gap-2 mt-0.5">
                  {p.is_premium && <Badge className="bg-amber-500/15 border-amber-500/30 text-amber-300 text-[10px] px-1.5 py-0">Premium</Badge>}
                  {p.country && <span className="text-[11px] text-zinc-500">{p.country}</span>}
                  {live && <span className="text-[10px] text-emerald-300 font-mono">● online</span>}
                </div>
              </div>
              {isOwner && !isMe && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => onKick(m.user_id)}
                  className="text-zinc-500 hover:text-rose-400 h-8 w-8 p-0"
                  title={isDE ? "Aus Squad entfernen" : "Kick from squad"}
                  data-testid={`squad-kick-${m.user_id}`}
                >
                  <UserMinus size={14} />
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      {inviteOpen && (
        <InviteFriendsModal
          isDE={isDE}
          squad={squad}
          myId={myId}
          onClose={() => setInviteOpen(false)}
          onSent={onInvited}
        />
      )}
    </section>
  );
}

// ============================================================================
// Create squad modal
// ============================================================================
function CreateSquadModal({ isDE, onClose, onCreated, ownerId }) {
  const [name, setName] = useState("");
  const [game, setGame] = useState("");
  const [mode, setMode] = useState("casual");
  const [maxSize, setMaxSize] = useState(4);
  const [busy, setBusy] = useState(false);
  const [games, setGames] = useState([]);

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data.items || [])).catch(() => {});
  }, []);

  const create = async () => {
    const clean = name.trim();
    if (clean.length < 2) {
      toast.error(isDE ? "Name ist zu kurz (min. 2 Zeichen)" : "Name too short (min 2 chars)");
      return;
    }
    setBusy(true);
    try {
      const { data, error } = await supabase
        .from("squads")
        .insert({
          name: clean.slice(0, MAX_NAME),
          owner_id: ownerId,
          game: game || null,
          mode: mode || null,
          max_size: maxSize,
        })
        .select("id")
        .single();
      if (error) throw error;
      toast.success(isDE ? "Squad erstellt!" : "Squad created!");
      onCreated?.(data.id);
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[squads] create failed", e);
      toast.error(isDE ? "Erstellen fehlgeschlagen" : "Couldn't create");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5" onClick={(e) => e.stopPropagation()} data-testid="squads-create-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg inline-flex items-center gap-2">
            <Swords size={16} className="text-neon" /> {isDE ? "Neuer Squad" : "New squad"}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-rose-400"><X size={16} /></button>
        </div>

        <div className="space-y-3.5">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">{isDE ? "Squad-Name" : "Squad name"}</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, MAX_NAME))}
              placeholder={isDE ? "Apex Predators" : "Apex Predators"}
              className="w-full rounded-xl bg-zinc-900 border border-white/10 px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-neon/50"
              data-testid="squads-create-name"
            />
            <p className="text-[10px] text-zinc-500 mt-1 text-right">{name.length}/{MAX_NAME}</p>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">{isDE ? "Spiel (optional)" : "Game (optional)"}</label>
            <select
              value={game}
              onChange={(e) => setGame(e.target.value)}
              className="w-full rounded-xl bg-zinc-900 border border-white/10 px-3 py-2.5 text-sm text-zinc-100 outline-none focus:border-neon/50"
              data-testid="squads-create-game"
            >
              <option value="">{isDE ? "Beliebig" : "Any"}</option>
              {games.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">{isDE ? "Modus" : "Mode"}</label>
            <div className="flex gap-2">
              {MODE_OPTIONS.map((m) => (
                <button
                  key={m.id} type="button"
                  onClick={() => setMode(m.id)}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold border transition ${
                    mode === m.id ? "bg-neon text-black border-neon shadow-neon" : "bg-white/5 border-white/10 text-zinc-300 hover:border-neon/40"
                  }`}
                  data-testid={`squads-create-mode-${m.id}`}
                >
                  {isDE ? m.de : m.en}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-500 mb-1 block">{isDE ? "Maximale Größe" : "Max size"}</label>
            <div className="flex gap-1.5 flex-wrap">
              {SIZE_OPTIONS.map((n) => (
                <button
                  key={n} type="button"
                  onClick={() => setMaxSize(n)}
                  className={`w-10 h-9 rounded-lg text-xs font-bold border transition ${
                    maxSize === n ? "bg-neon text-black border-neon shadow-neon" : "bg-white/5 border-white/10 text-zinc-300 hover:border-neon/40"
                  }`}
                  data-testid={`squads-create-size-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={create}
            disabled={busy || name.trim().length < 2}
            className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold w-full mt-2"
            data-testid="squads-create-submit"
          >
            {busy ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} className="mr-2" />}
            {isDE ? "Squad gründen" : "Create squad"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Invite friends modal
// ============================================================================
function InviteFriendsModal({ isDE, squad, myId, onClose, onSent }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sentSet, setSentSet] = useState(new Set());
  const [myName, setMyName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // Fetch my friendships (accepted) and resolve other-side profiles
        const { data } = await supabase
          .from("friendships")
          .select("requester_id, addressee_id, status")
          .or(`requester_id.eq.${myId},addressee_id.eq.${myId}`)
          .eq("status", "accepted");
        const friendIds = (data || [])
          .map((r) => r.requester_id === myId ? r.addressee_id : r.requester_id);
        const memberIds = new Set(squad.members.map((m) => m.user_id));
        const targetIds = friendIds.filter((id) => !memberIds.has(id));

        // Filter out already-invited (pending) for this squad
        let alreadyInvited = new Set();
        if (targetIds.length) {
          const { data: invs } = await supabase
            .from("squad_invites")
            .select("invitee_id, status")
            .eq("squad_id", squad.id)
            .eq("status", "pending")
            .in("invitee_id", targetIds);
          (invs || []).forEach((i) => alreadyInvited.add(i.invitee_id));
        }

        if (targetIds.length === 0) {
          setFriends([]);
        } else {
          const { data: profs } = await supabase
            .from("profiles")
            .select("user_id, username, avatar_url, country, is_premium")
            .in("user_id", targetIds);
          setFriends((profs || []).map((p) => ({ ...p, _invited: alreadyInvited.has(p.user_id) })));
        }

        // Get my username for nicer notification title
        const { data: mine } = await supabase.from("profiles").select("username").eq("user_id", myId).maybeSingle();
        setMyName(mine?.username || "");
      } catch (_e) { setFriends([]); }
      finally { setLoading(false); }
    })();
  }, [squad.id, myId]); // eslint-disable-line

  const invite = async (target) => {
    setSentSet((s) => new Set(s).add(target.user_id));
    try {
      const { error } = await supabase.from("squad_invites").insert({
        inviter_id: myId,
        invitee_id: target.user_id,
        squad_id: squad.id,
        game: squad.game,
        mode: squad.mode,
        status: "pending",
      });
      if (error) throw error;
      toast.success(isDE ? "Einladung gesendet" : "Invite sent");
      api.post("/squads/notify", {
        invitee_user_id: target.user_id,
        kind: "squad_invite",
        inviter_username: myName,
        squad_name: squad.name,
      }).catch(() => {});
      onSent?.();
    } catch (_e) {
      setSentSet((s) => { const n = new Set(s); n.delete(target.user_id); return n; });
      toast.error(isDE ? "Konnte nicht senden" : "Couldn't send");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-950 p-5" onClick={(e) => e.stopPropagation()} data-testid="squads-invite-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-base inline-flex items-center gap-2">
            <Users size={14} className="text-neon" /> {isDE ? "Freunde einladen" : "Invite friends"}
          </h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-rose-400"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-zinc-500 mb-3">
          {isDE
            ? `Lade Freunde in „${squad.name}" ein. Sie erhalten eine Echtzeit-Benachrichtigung.`
            : `Invite friends to "${squad.name}". They'll get a real-time notification.`}
        </p>

        <div className="max-h-80 overflow-y-auto -mx-1">
          {loading ? (
            <p className="text-xs text-zinc-500 px-3 py-6 text-center">
              <Loader2 className="animate-spin inline mr-2" size={12} /> {isDE ? "Lade Freunde…" : "Loading friends…"}
            </p>
          ) : friends.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-xs text-zinc-500 mb-2">
                {isDE ? "Keine einladbaren Freunde." : "No friends to invite."}
              </p>
              <Link to="/app/friends" onClick={onClose} className="text-xs text-neon hover:underline" data-testid="squads-invite-find-friends">
                {isDE ? "Freunde finden →" : "Find friends →"}
              </Link>
            </div>
          ) : (
            <ul className="space-y-1">
              {friends.map((f) => {
                const sent = sentSet.has(f.user_id) || f._invited;
                return (
                  <li key={f.user_id} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5" data-testid={`squads-invite-row-${f.user_id}`}>
                    <Avatar src={f.avatar_url} name={f.username} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{f.username}</p>
                      <p className="text-[10px] text-zinc-500">{f.country || ""}</p>
                    </div>
                    <Button
                      size="sm" onClick={() => invite(f)} disabled={sent}
                      className={`rounded-full font-bold h-7 text-[11px] ${
                        sent ? "bg-zinc-800 text-zinc-500" : "bg-neon text-black hover:bg-neon-hover"
                      }`}
                      data-testid={`squads-invite-send-${f.user_id}`}
                    >
                      {sent ? <Check size={12} /> : <><MessageSquare size={11} className="mr-1" /> {isDE ? "Einladen" : "Invite"}</>}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
